import React, { useState, useEffect, useCallback } from 'react';
import { getStatusChipSx } from '../../../constants/statusColors';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  VisibilityOutlined as VisibilityIcon,
  PersonOutlined as PersonIcon,
  PersonRemoveOutlined as EndIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../../services/api';
import { formatDisplayDate, mapDirectorFromApi } from '../../../utils/apiMappers';
import EndAppointmentModal from '../../../components/directors/EndAppointmentModal';

const mapDirectorRow = (d) => {
  const m = mapDirectorFromApi(d);
  const displayName = m.full_name || `${m.firstName} ${m.lastName}`.trim();
  const appointmentActive = m.is_active === true || m.is_active === 1 || m.status === 'ACTIVE';
  return {
    id: m.id,
    fullName: displayName,
    firstName: m.firstName,
    lastName: m.lastName,
    position: m.position,
    emiratesId: m.emiratesId,
    appointmentDate: d.appointment_date,
    status: appointmentActive ? 'ACTIVE' : 'ENDED',
    isActive: appointmentActive,
  };
};

const DirectorsTab = ({ companyId, companyName }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeDirectors, setActiveDirectors] = useState([]);
  const [pastDirectors, setPastDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [endTarget, setEndTarget] = useState(null);

  const fetchDirectors = useCallback(async () => {
    setLoading(true);
    setError('');

    if (companyId) {
      try {
        const res = await csAdminApi.getCompanyDirectors(companyId, { include_ended: 1 });
        const rows = (res.data?.data || []).map(mapDirectorRow);
        setActiveDirectors(rows.filter((r) => r.isActive));
        setPastDirectors(rows.filter((r) => !r.isActive));
        return;
      } catch (err) {
        console.error('Company directors API:', err);
        setError('Failed to load directors');
        setActiveDirectors([]);
        setPastDirectors([]);
        return;
      } finally {
        setLoading(false);
      }
    }

    setActiveDirectors([]);
    setPastDirectors([]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    fetchDirectors();
  }, [fetchDirectors]);

  const handleEndAppointment = async (payload) => {
    if (!endTarget) return;
    await csAdminApi.endDirectorAppointment(endTarget.id, {
      company_id: companyId,
      ...payload,
    });
    enqueueSnackbar('Director appointment ended', { variant: 'success' });
    setEndTarget(null);
    await fetchDirectors();
  };

  const renderTable = (directors, { showEndAction = false } = {}) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Position</TableCell>
            <TableCell>Emirates ID</TableCell>
            <TableCell>Appointment Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {directors.map((director) => (
            <TableRow key={director.id} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      bgcolor: '#E3F2FD',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 24, color: '#1976D2' }} />
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {director.fullName || `${director.firstName} ${director.lastName}`.trim()}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{director.position}</TableCell>
              <TableCell>{director.emiratesId}</TableCell>
              <TableCell>{formatDisplayDate(director.appointmentDate)}</TableCell>
              <TableCell>
                <Chip
                  label={director.status}
                  sx={{
                    ...(director.isActive
                      ? getStatusChipSx('ACTIVE')
                      : { bgcolor: '#F5F5F5', color: '#757575' }),
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                  size="small"
                />
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/directors/${director.id}`)}
                  title="View Details"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
                {director.isActive && (
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/directors/${director.id}/edit`)}
                    title="Edit"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {showEndAction && director.isActive && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setEndTarget(director)}
                    title="End appointment"
                  >
                    <EndIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Board of Directors</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => navigate(`/directors/add?companyId=${companyId}`)}
        >
          Add Director
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            Current directors ({activeDirectors.length})
          </Typography>
          {activeDirectors.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
              No active directors assigned
            </Typography>
          ) : (
            renderTable(activeDirectors, { showEndAction: true })
          )}

          {pastDirectors.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Past directors ({pastDirectors.length})
              </Typography>
              {renderTable(pastDirectors)}
            </Box>
          )}
        </>
      )}

      <EndAppointmentModal
        open={Boolean(endTarget)}
        onClose={() => setEndTarget(null)}
        onSubmit={handleEndAppointment}
        companyName={companyName}
        title="End director appointment"
        description="This ends the board appointment at this company only. The person remains in the director pool and history."
      />
    </Paper>
  );
};

export default DirectorsTab;
